import { createSlice } from "@reduxjs/toolkit";

import {
  getStudents,
  createStudent,
  createMultipleStudents,
  updateStudent,
  updateClassForStudent,
  deleteStudent,
  changeProfilePicture,
  getStudentsBeenInClass,
  updateGraduationDate,
} from "./studentAction";
import { StudentSortValue } from "config/constants";
import type { Student } from "types/Student";

interface studentState {
  loading: boolean;
  students: Student[];
  sortBy: StudentSortValue;
  selectedStudents: Student[];
  error: string;
  success: boolean;
}

const initialState: studentState = {
  loading: false,
  students: [],
  sortBy: StudentSortValue.name_asc,
  selectedStudents: [],
  error: "",
  success: false,
};

const studentSlice = createSlice({
  name: "student",
  initialState,
  reducers: {
    setStudents: (
      state,
      action: { type: string; payload: { students: Student[] } }
    ) => {
      state.students = action.payload.students;
    },
    setSortBy: (
      state,
      action: { type: string; payload: { sortBy: StudentSortValue } }
    ) => {
      state.sortBy = action.payload.sortBy;
    },
    resetSortBy: (state) => {
      state.sortBy = StudentSortValue.name_asc;
    },
    setSelectedStudents: (
      state,
      action: { type: string; payload: { isAdd: boolean; student: Student } }
    ) => {
      if (action.payload.isAdd) {
        state.selectedStudents = [
          ...state.selectedStudents,
          action.payload.student,
        ];
      } else {
        const index = state.selectedStudents.findIndex(
          (student) => student.id === action.payload.student.id
        );
        state.selectedStudents.splice(index, 1);
      }
    },
    resetSelectedStudents: (state) => {
      state.selectedStudents = [];
    },
    setNewClass: (
      state,
      action: { type: string; payload: { classId: number } }
    ) => {
      for (let i = 0; i < state.selectedStudents.length; i++) {
        state.selectedStudents[i].class_id = action.payload.classId;
      }
    },
    resetStudent: (state) => {
      state.students = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getStudents.pending, (state) => {
      state.loading = true;
      state.success = false;
      state.students = [];
    });
    builder.addCase(getStudents.fulfilled, (state, { payload }) => {
      state.students = payload.students;
      state.success = true;
      state.loading = false;
    });
    builder.addCase(getStudents.rejected, (state, { payload }) => {
      state.error = payload || "_generic._unknown_error";
      state.loading = false;
    });
    builder.addCase(createStudent.pending, (state) => {
      state.loading = true;
      state.success = false;
    });
    builder.addCase(createStudent.fulfilled, (state, { payload }) => {
      state.students = [...state.students, payload.student];
      state.success = true;
      state.loading = false;
    });
    builder.addCase(createStudent.rejected, (state, { payload }) => {
      state.error = payload || "_generic._unknown_error";
      state.loading = false;
    });
    builder.addCase(createMultipleStudents.pending, (state) => {
      state.loading = true;
      state.success = false;
    });
    builder.addCase(createMultipleStudents.fulfilled, (state, { payload }) => {
      state.students = [...state.students, ...payload.students];
      state.success = true;
      state.loading = false;
    });
    builder.addCase(createMultipleStudents.rejected, (state, { payload }) => {
      state.error = payload || "_generic._unknown_error";
      state.loading = false;
    });
    builder.addCase(updateStudent.pending, (state) => {
      state.loading = true;
      state.success = false;
    });
    builder.addCase(updateStudent.fulfilled, (state, { payload }) => {
      const index = state.students.findIndex(
        (student) => student.id === payload.student.id
      );
      if (index >= 0) state.students[index] = payload.student;
      state.success = true;
      state.loading = false;
    });
    builder.addCase(updateStudent.rejected, (state, { payload }) => {
      state.error = payload || "_generic._unknown_error";
      state.loading = false;
    });
    builder.addCase(updateClassForStudent.pending, (state) => {
      state.loading = true;
      state.success = false;
    });
    builder.addCase(updateClassForStudent.fulfilled, (state, { payload }) => {
      payload.formData.student_ids.forEach((studentId) => {
        const index = state.students.findIndex(
          (student) => student.id === studentId
        );

        if (index >= 0) {
          state.students[index] = {
            ...state.students[index],
            class_id: payload.formData.class_id,
          };
        }
      });
      state.success = true;
      state.loading = false;
      state.selectedStudents = [];
    });
    builder.addCase(updateClassForStudent.rejected, (state, { payload }) => {
      state.error = payload || "_generic._unknown_error";
      state.loading = false;
    });
    builder.addCase(changeProfilePicture.pending, (state) => {
      state.loading = true;
      state.success = false;
    });
    builder.addCase(changeProfilePicture.fulfilled, (state, { payload }) => {
      const index = state.students.findIndex(
        (student) => student.id === payload.student.id
      );
      if (index >= 0) state.students[index] = payload.student;
      state.success = true;
      state.loading = false;
    });
    builder.addCase(changeProfilePicture.rejected, (state, { payload }) => {
      state.error = payload || "_generic._unknown_error";
      state.loading = false;
    });
    builder.addCase(deleteStudent.pending, (state) => {
      state.loading = true;
      state.success = false;
    });
    builder.addCase(deleteStudent.fulfilled, (state, { payload }) => {
      state.success = true;
      state.loading = false;
      const index = state.students.findIndex(
        (student) => student.id === payload.id
      );
      if (index >= 0) state.students.splice(index, 1);
    });
    builder.addCase(deleteStudent.rejected, (state, { payload }) => {
      state.error = payload || "_generic._unknown_error";
      state.loading = false;
    });
    builder.addCase(getStudentsBeenInClass.pending, (state) => {
      state.loading = true;
      state.success = false;
      state.students = [];
    });
    builder.addCase(getStudentsBeenInClass.fulfilled, (state, { payload }) => {
      state.students = payload.students;
      state.success = true;
      state.loading = false;
    });
    builder.addCase(getStudentsBeenInClass.rejected, (state, { payload }) => {
      state.error = payload || "_generic._unknown_error";
      state.loading = false;
    });
    builder.addCase(updateGraduationDate.pending, (state) => {
      state.loading = true;
      state.success = false;
    });
    builder.addCase(updateGraduationDate.fulfilled, (state, { payload }) => {
      const index = state.students.findIndex(
        (student) => student.id === payload.student.id
      );
      if (index >= 0) state.students[index] = payload.student;
      state.success = true;
      state.loading = false;
    });
    builder.addCase(updateGraduationDate.rejected, (state, { payload }) => {
      state.error = payload || "_generic._unknown_error";
      state.loading = false;
    });
  },
});

export const {
  setStudents,
  setSortBy,
  resetSortBy,
  setSelectedStudents,
  resetSelectedStudents,
  setNewClass,
  resetStudent,
} = studentSlice.actions;
export default studentSlice.reducer;
